import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { preReleaseService, PreReleaseProject } from '../../services/preReleaseService';
import NotificationAnalytics from '../../components/notifications/NotificationAnalytics';
import '../../styles/pages/NotificationAnalyticsPage.css';

interface NotificationAnalyticsPageProps {}

const NotificationAnalyticsPage: React.FC<NotificationAnalyticsPageProps> = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState<PreReleaseProject | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (projectId) {
      loadProject();
    }
  }, [user, projectId, navigate]);

  const loadProject = async () => {
    if (!projectId) return;

    try {
      setLoading(true);
      setError(null);

      const projectData = await preReleaseService.getProjectById(projectId);
      
      // Check if user owns this project or has permission to view analytics
      if (!projectData) {
        setError('Project not found.');
        return;
      }

      if (projectData.publisher_id !== user?.id) {
        setError('You do not have permission to view analytics for this project.');
        return;
      }

      setProject(projectData);
    } catch (err: any) {
      console.error('Error loading project:', err);
      setError(err.message || 'Failed to load project data');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToProject = () => {
    if (projectId) {
      navigate(`/pre-release/${projectId}`);
    } else {
      navigate('/publisher-dashboard');
    }
  };

  const handleBackToDashboard = () => {
    navigate('/publisher-dashboard');
  };

  if (loading) {
    return (
      <div className="notification-analytics-page">
        <div className="container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading notification analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="notification-analytics-page">
        <div className="container">
          <div className="error-state">
            <h2>Error Loading Analytics</h2>
            <p>{error}</p>
            <div className="error-actions">
              <button onClick={loadProject} className="retry-button">
                Try Again
              </button>
              <button onClick={handleBackToDashboard} className="back-button">
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="notification-analytics-page">
        <div className="container">
          <div className="error-state">
            <h2>Project Not Found</h2>
            <p>The requested project could not be found.</p>
            <button onClick={handleBackToDashboard} className="back-button">
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="notification-analytics-page">
      <div className="container">
        <div className="page-header">
          <div className="page-title-section">
            <button 
              onClick={handleBackToProject} 
              className="back-link"
              aria-label="Back to project details"
            >
              ← Back to Project
            </button>
            <h1>Notification Analytics</h1>
            <p className="project-title">
              Analytics for: <strong>{project.title}</strong>
            </p>
          </div>
          
          <div className="page-actions">
            <button 
              onClick={handleBackToDashboard} 
              className="dashboard-link"
            >
              📊 Publisher Dashboard
            </button>
          </div>
        </div>

        <div className="analytics-content">
          <NotificationAnalytics 
            projectId={project.id} 
            projectTitle={project.title}
          />
        </div>

        <div className="page-footer">
          <div className="footer-actions">
            <button 
              onClick={handleBackToProject} 
              className="secondary-button"
            >
              View Project Details
            </button>
            <button 
              onClick={() => navigate(`/pre-release/${projectId}/contact-history`)} 
              className="primary-button"
            >
              View Contact History
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationAnalyticsPage;
